import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LS_DISMISSED = 'rvs_newsletter_dismissed';
const LS_SUBSCRIBED = 'rvs_newsletter_subscribed';
const LS_COOKIE_CONSENT = 'posthog_consent'; // matches CookieConsent.js key

const NewsletterPopup = () => {
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        // Gate checks: already dismissed, subscribed, or no cookie consent
        const dismissed = localStorage.getItem(LS_DISMISSED);
        const subscribed = localStorage.getItem(LS_SUBSCRIBED);
        const cookieConsent = localStorage.getItem(LS_COOKIE_CONSENT);

        if (dismissed || subscribed || cookieConsent !== 'true') {
            return;
        }

        const timer = setTimeout(() => {
            setVisible(true);
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    const handleDismiss = () => {
        setVisible(false);
        localStorage.setItem(LS_DISMISSED, 'true');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setStatus('loading');
        setErrorMsg('');

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const res = await fetch(`${apiUrl}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            if (res.ok) {
                setStatus('success');
                localStorage.setItem(LS_SUBSCRIBED, 'true');
                setTimeout(() => {
                    setVisible(false);
                }, 2000);
            } else {
                const data = await res.json().catch(() => ({}));
                setStatus('error');
                setErrorMsg(data.detail || 'Something went wrong. Please try again.');
            }
        } catch {
            setStatus('error');
            setErrorMsg('Network error. Please try again later.');
        }
    };

    return (
        <AnimatePresence>
            {visible && (
                <>
                    {/* Mobile responsive styles */}
                    <style>{`
                        @media (max-width: 768px) {
                            .rvs-nl-popup {
                                padding: 24px 24px 24px !important;
                                max-width: 90vw !important;
                                width: 90vw !important;
                            }
                            .rvs-nl-popup h2 {
                                font-size: 24px !important;
                            }
                        }
                    `}</style>

                    {/* Backdrop overlay */}
                    <motion.div
                        key="nl-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={handleDismiss}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 9998,
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            backdropFilter: 'blur(4px)',
                            WebkitBackdropFilter: 'blur(4px)',
                        }}
                    />

                    {/* Centering wrapper — uses flexbox instead of transform so Framer Motion doesn't conflict */}
                    <motion.div
                        key="nl-popup-wrapper"
                        initial={{ opacity: 0, scale: 0.92, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 30 }}
                        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            padding: '16px',
                            boxSizing: 'border-box',
                        }}
                    >
                        {/* Popup */}
                        <div
                            className="rvs-nl-popup"
                            style={{
                                position: 'relative',
                                width: '90%',
                                maxWidth: '440px',
                                background: 'rgba(8, 8, 8, 0.97)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '16px',
                                padding: '40px 32px 32px',
                                boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 80px rgba(255,255,255,0.02)',
                                pointerEvents: 'auto',
                                overflowX: 'hidden',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                boxSizing: 'border-box',
                            }}
                        >
                            {/* Close X */}
                            <button
                                onClick={handleDismiss}
                                aria-label="Close newsletter popup"
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    background: 'none',
                                    border: 'none',
                                    color: 'rgba(255,255,255,0.3)',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    lineHeight: 1,
                                    transition: 'color 0.2s',
                                }}
                                onMouseEnter={e => e.target.style.color = 'white'}
                                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.3)'}
                            >
                                ✕
                            </button>

                            {status === 'success' ? (
                                /* Success State */
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    style={{ textAlign: 'center', padding: '20px 0' }}
                                >
                                    <div style={{
                                        fontSize: '36px',
                                        fontWeight: 900,
                                        color: 'white',
                                        letterSpacing: '-0.02em',
                                        marginBottom: '8px',
                                    }}>
                                        YOU'RE IN.
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: 'rgba(255,255,255,0.4)',
                                        letterSpacing: '0.05em',
                                    }}>
                                        Welcome to the crew.
                                    </div>
                                </motion.div>
                            ) : (
                                /* Form State */
                                <>
                                    {/* Label */}
                                    <div style={{
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '0.3em',
                                        color: 'rgba(255,255,255,0.35)',
                                        textTransform: 'uppercase',
                                        marginBottom: '12px',
                                    }}>
                                        Stay in the loop
                                    </div>

                                    {/* Headline */}
                                    <h2 style={{
                                        fontSize: '28px',
                                        fontWeight: 900,
                                        color: 'white',
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1.1,
                                        marginBottom: '12px',
                                        textTransform: 'uppercase',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                    }}>
                                        GET EXCLUSIVE UPDATES
                                    </h2>

                                    {/* Subtext */}
                                    <p style={{
                                        fontSize: '14px',
                                        lineHeight: 1.6,
                                        color: 'rgba(255,255,255,0.45)',
                                        marginBottom: '28px',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                    }}>
                                        Behind-the-scenes content, new project drops,
                                        and industry insights — straight to your inbox.
                                    </p>

                                    {/* Form */}
                                    <form onSubmit={handleSubmit}>
                                        <input
                                            type="email"
                                            required
                                            placeholder="your@email.com"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            disabled={status === 'loading'}
                                            style={{
                                                width: '100%',
                                                padding: '14px 16px',
                                                backgroundColor: 'rgba(255,255,255,0.04)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '8px',
                                                color: 'white',
                                                fontSize: '14px',
                                                outline: 'none',
                                                marginBottom: '12px',
                                                transition: 'border-color 0.2s',
                                                boxSizing: 'border-box',
                                            }}
                                            onFocus={e => e.target.style.borderColor = 'rgba(255,255,255,0.3)'}
                                            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                                        />

                                        {/* Error message */}
                                        {status === 'error' && errorMsg && (
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#ef4444',
                                                marginBottom: '12px',
                                                padding: '0 2px',
                                            }}>
                                                {errorMsg}
                                            </div>
                                        )}

                                        {/* Submit Button */}
                                        <button
                                            type="submit"
                                            disabled={status === 'loading'}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '12px',
                                                fontWeight: 900,
                                                letterSpacing: '0.15em',
                                                textTransform: 'uppercase',
                                                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                                                opacity: status === 'loading' ? 0.6 : 1,
                                                transition: 'opacity 0.2s, background-color 0.2s',
                                                boxSizing: 'border-box',
                                            }}
                                            onMouseEnter={e => { if (status !== 'loading') e.target.style.backgroundColor = '#e0e0e0'; }}
                                            onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                                        >
                                            {status === 'loading' ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
                                        </button>
                                    </form>

                                    {/* No thanks link */}
                                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                        <button
                                            onClick={handleDismiss}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'rgba(255,255,255,0.25)',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                letterSpacing: '0.05em',
                                                transition: 'color 0.2s',
                                            }}
                                            onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.5)'}
                                            onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.25)'}
                                        >
                                            No thanks
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default NewsletterPopup;
